import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useSubscription } from '@/hooks/subscription';
import { useAuth } from '@/contexts/auth';
import { useUserProfile } from '@/contexts/profile/ProfileContext';
import UserDropdownHeader from "./dropdown/UserDropdownHeader";
import UserDropdownItems from "./dropdown/UserDropdownItems";
import { useTranslation } from "react-i18next";

import type { AuthUserLite, UserMetadata } from '@/types/domain';

interface UserDropdownMenuProps {
  user: AuthUserLite;
  signOut: () => void;
  isAdmin: boolean;
}

const UserDropdownMenu = ({ user, signOut, isAdmin }: UserDropdownMenuProps) => {
  const { t } = useTranslation('common');
  const { profile } = useUserProfile();
  const { data: subscriptionStatus } = useSubscription();
  const { user: authUser } = useAuth();
  const dropdownItemClassName =
    "rounded-md px-3 py-2.5 text-sm font-medium text-brand-ink focus:bg-brand-mist focus:text-brand-ink";
  const meta = authUser?.user_metadata as UserMetadata | undefined;

  const getAvatarText = () => {
    const initialSource = [
      profile?.first_name,
      profile?.last_name,
      meta?.first_name,
      meta?.last_name,
      authUser?.email,
    ].find((value) => typeof value === 'string' && value.trim().length > 0);

    return initialSource?.trim().charAt(0).toUpperCase() || 'U';
  };
  const getUserDisplayName = () => {
    if (profile?.first_name || profile?.last_name) {
      const fullName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim();
      if (fullName) return fullName;
    }
    const metaFirst = meta?.first_name || '';
    const metaLast = meta?.last_name || '';
    const metaFull = `${metaFirst} ${metaLast}`.trim();
    return metaFull || authUser?.email || 'User';
  };
  const getReferralLink = () => {
    if (!authUser?.id) return '';
    const referralUrl = `${window.location.origin}/auth?mode=signup&ref=${authUser.id}`;
    const text = encodeURIComponent("I'm inviting you to join WhatsGood HK! Sign up using my link:");
    return `https://wa.me/?text=${text}%20${encodeURIComponent(referralUrl)}`;
  };
  const getFormattedTierName = () => {
    const tier = subscriptionStatus?.tier || 'free_tier';
    switch (tier) {
      case 'premium_tier': return 'Premium';
      case 'whatsapp_tier': return 'WhatsApp Verified';
      default: return 'Starter';
    }
  };
  const hasProfileName = !!(profile?.first_name || profile?.last_name || meta?.first_name || meta?.last_name);
  const hasPendingVerification = subscriptionStatus?.status === 'requires_action';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="h-9 gap-2 rounded-md border-brand-cloud bg-white px-2 text-brand-ink hover:bg-brand-mist hover:text-brand-ink"
          aria-label="Open account menu"
        >
          <Avatar className="h-7 w-7 border border-brand-cloud">
            <AvatarImage src={user?.user_metadata?.avatar_url || ""} alt="Profile" />
            <AvatarFallback className="bg-brand-mist text-xs font-semibold text-brand-ink">{getAvatarText()}</AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium">Account</span>
          <ChevronDown className="h-4 w-4 text-brand-slate" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-72 rounded-md border-brand-cloud bg-white p-2 text-brand-ink shadow-lg"
        align="end"
        sideOffset={10}
        forceMount
      >
        <UserDropdownHeader
          displayName={getUserDisplayName()}
          email={user.email}
          whatsappNumber={profile?.whatsapp_number || authUser?.whatsapp_number || null}
          hasProfileName={hasProfileName}
          tierName={getFormattedTierName()}
          hasPendingVerification={hasPendingVerification}
          pendingLabel={t("userMenu.paymentPending")}
        />
        <DropdownMenuSeparator className="mx-1 my-2 bg-brand-cloud" />
        <UserDropdownItems referralLink={getReferralLink()} />
        
        {!isAdmin && !hasPendingVerification && subscriptionStatus?.tier === 'free_tier' && (
          <>
            <DropdownMenuSeparator className="mx-1 my-2 bg-brand-cloud" />
            <DropdownMenuItem className={dropdownItemClassName} asChild>
              <Link to="/profile?tab=subscription">
                <span>{t("userMenu.upgradeToPremium")}</span>
              </Link>
            </DropdownMenuItem>
          </>
        )}
        
        {isAdmin && (
          <>
            <DropdownMenuSeparator className="mx-1 my-2 bg-brand-cloud" />
            <DropdownMenuItem className={dropdownItemClassName} asChild>
              <Link to="/admin">
                <span>{t("navigation.admin")}</span>
              </Link>
            </DropdownMenuItem>
          </>
        )}
        
        <DropdownMenuSeparator className="mx-1 my-2 bg-brand-cloud" />
        <DropdownMenuItem 
          className="rounded-md px-3 py-2.5 text-sm font-medium text-red-600 focus:bg-red-50 focus:text-red-700"
          onClick={signOut}
        >
          <span>{t("userMenu.logout")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserDropdownMenu;
